import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Alert, Badge, Box, Button, Chip, Divider, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import NotificationList, { openRenouncedOrder } from './NotificationList';
import { getSocket } from '../../../../lib/socket';
import { useSessionUser } from '../ProfileSection/userInfo';
import { IconBell, IconChecks } from '@tabler/icons';

const parseArrayMaybeDoubleEncoded = (input) => {
  if (!input) return [];
  let v = input;
  for (let i = 0; i < 2 && typeof v === 'string'; i++) {
    try {
      v = JSON.parse(v);
    } catch {
      break;
    }
  }
  return Array.isArray(v) ? v : [];
};

// role may be a string or an array; a missing role never matches
const hasRole = (role, name) => (Array.isArray(role) || typeof role === 'string') && !!name && role.includes(name);

const classifyNotification = (n, user) => {
  if (!n || !user) return 'pending';

  const typeName = n?.NotificationType?.name || '';
  const ref = n?.reference?.data || {};
  const role = user?.role;
  const meId = String(user.id);

  if (hasRole(role, process.env.REACT_APP_TC_ADMIN_ROLE_NAME)) {
    if (typeName === 'supervisor_confirmed_specification' || typeName === 'executive_confirmed_contract') return 'confirmed';
    if (typeName === 'supervisor_declined_specification' || typeName === 'executive_rejected_contract') return 'rejected';
    return 'pending';
  }

  if (hasRole(role, process.env.REACT_APP_SUPERVISOR_ROLE_NAME)) {
    if (String(ref.supervisor_id) === meId) {
      if (ref.supervisorConfirmed) return 'confirmed';
      if (ref.supervisorRejected) return 'rejected';
    }

    const extraConfirmed = parseArrayMaybeDoubleEncoded(ref.additionalSupervisors).some(
      (s) => String(s.id) === meId && s.confirmed === true
    );

    const extraRejected = parseArrayMaybeDoubleEncoded(ref.additionalSupervisorRejected).some(
      (s) => String(s.id) === meId && (s.declined === true || s.executive_rejected === true)
    );

    if (extraConfirmed) return 'confirmed';
    if (extraRejected) return 'rejected';
    return 'pending';
  }

  if (hasRole(role, process.env.REACT_APP_EXECUTIVE_ROLE_NAME)) {
    if (ref.executive_confirmed) return 'confirmed';
    if (ref.executive_rejected) return 'rejected';
    return 'pending';
  }

  if (hasRole(role, process.env.REACT_APP_CONTRACTS_MANAGER_ROLE_NAME)) {
    if (typeName === 'executive_confirmed_contract') return 'confirmed';
    if (typeName === 'executive_rejected_contract') return 'rejected';
    return 'pending';
  }

  return 'pending';
};

const BASE_FILTERS = [
  { value: 'all', label: 'Visi' },
  { value: 'unread', label: 'Neperskaityti' },
  { value: 'read', label: 'Perskaityti' },
  { value: 'confirmed', label: 'Patvirtinti' },
  { value: 'pending', label: 'Nepatvirtinti' },
  { value: 'rejected', label: 'Atmesti' }
];

const getFiltersForRole = (role) => {
  const noPending =
    hasRole(role, process.env.REACT_APP_CONTRACTS_MANAGER_ROLE_NAME) || hasRole(role, process.env.REACT_APP_TC_ADMIN_ROLE_NAME);

  return noPending ? BASE_FILTERS.filter((f) => f.value !== 'pending') : BASE_FILTERS;
};

// where the panel opens, depending on where the bell is
const POSITIONS = {
  // top bar (organization users, phones): below the bell, right-aligned
  header: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
    paperSx: { mt: 1.5 }
  },
  // sidebar footer: to the right of the sidebar, rising from the bell
  sidebar: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
    paperSx: { ml: 3 }
  }
};

// ==============================|| NOTIFICATIONS: bell + panel ||============================== //

export default function NotificationSection({ variant = 'header', userData: userDataProp }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const userData = useSessionUser(userDataProp);
  // where to open: decided from where the bell actually is when clicked
  // (left half of the screen = sidebar → open to the right; otherwise open below)
  const [side, setSide] = useState(variant === 'header' ? 'header' : 'sidebar');
  const position = POSITIONS[side];

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [error, setErrorMessage] = useState('');
  const [notifications, setNotifications] = useState({ notifications: [], unreadCount: 0 });

  const anchorRef = useRef(null);

  const retrieveUserNotifications = useCallback(async () => {
    try {
      const res = await axios.get('/api/db/getUserNotifications', { params: { type: 'all' } });
      if (res.data.result) setNotifications(res.data.result);
    } catch (err) {
      setErrorMessage(err?.response?.data || err.message);
      setTimeout(() => setErrorMessage(''), 8000);
    }
  }, []);

  // load once on page load so the bell shows the real unread count…
  useEffect(() => {
    retrieveUserNotifications();
  }, [retrieveUserNotifications]);

  // …and refresh every time the panel opens
  useEffect(() => {
    if (open) retrieveUserNotifications();
  }, [open, retrieveUserNotifications]);

  // live updates
  useEffect(() => {
    const s = getSocket();
    const base = (p) => p ?? { notifications: [], unreadCount: 0 };

    s.on('notifications:new', (n) => {
      setNotifications((prev) => {
        const b = base(prev);
        if (b.notifications.some((x) => x.id === n.id)) return b;
        return { notifications: [n, ...b.notifications], unreadCount: b.unreadCount + 1 };
      });
    });

    s.on('notifications:readAll', ({ ids, unreadCount }) => {
      setNotifications((prev) => {
        const b = base(prev);
        const idSet = new Set(ids);
        return {
          notifications: b.notifications.map((n) => (idSet.has(n.id) ? { ...n, is_read: true, read: true } : n)),
          unreadCount: unreadCount ?? 0
        };
      });
    });

    s.on('notifications:read', ({ id, unreadCount }) => {
      setNotifications((prev) => {
        const b = base(prev);
        let wasUnread = false;

        const updated = b.notifications.map((n) => {
          if (n.id === id) {
            if (!n.is_read && !n.read) wasUnread = true;
            return { ...n, is_read: true, read: true };
          }
          return n;
        });

        return {
          notifications: updated,
          unreadCount: typeof unreadCount === 'number' ? unreadCount : Math.max(0, b.unreadCount - (wasUnread ? 1 : 0))
        };
      });
    });

    return () => {
      s.off('notifications:new');
      s.off('notifications:readAll');
      s.off('notifications:read');
    };
  }, []);

  const filteredNotifications = useMemo(() => {
    if (!userData) return notifications.notifications;

    return notifications.notifications.filter((n) => {
      const unread = !n.is_read && !n.read;
      const read = !unread;
      const status = classifyNotification(n, userData);

      switch (filter) {
        case 'unread':
          return unread;
        case 'read':
          return read;
        case 'confirmed':
          return status === 'confirmed';
        case 'pending':
          return status === 'pending';
        case 'rejected':
          return status === 'rejected';
        default:
          return true;
      }
    });
  }, [notifications, filter, userData]);

  const markAllAsRead = async () => {
    setNotifications((prev) => ({
      notifications: prev.notifications.map((n) => ({ ...n, is_read: true, read: true })),
      unreadCount: 0
    }));

    try {
      await axios.put('/api/notifications/markAllRead');
    } catch {
      retrieveUserNotifications();
    }
  };

  const handleItemClick = useCallback(
    async (n) => {
      setOpen(false);

      setNotifications((prev) => {
        const wasUnread = prev.notifications.some((x) => x.id === n.id && !x.is_read && !x.read);

        return {
          notifications: prev.notifications.map((x) => (x.id === n.id ? { ...x, is_read: true, read: true } : x)),
          unreadCount: wasUnread ? Math.max(0, prev.unreadCount - 1) : prev.unreadCount
        };
      });

      try {
        await axios.post(`/api/db/${n.id}/read`);
      } catch {
        // ignore
      }

      const ref = n.reference?.data;
      const type = n.reference_type;
      const name = n.NotificationType?.name;

      // service renounced (reference = the order): the same review page as a specification
      if (name === 'service_renunciated') {
        try {
          await openRenouncedOrder(n, navigate);
        } catch (e) {
          setOpen(true);
          setErrorMessage(e?.message || 'Nepavyko atidaryti pranešimo.');
          setTimeout(() => setErrorMessage(''), 8000);
        }
        return;
      }

      switch (type) {
        case 'specification':
          if (name === 'supervisor_declined_specification') {
            navigate(`/organization/${ref.Organization.name}`);
          } else {
            navigate(`/peržiūrėti/${type}/${ref.id}`, { state: { spec: ref, notification: n } });
          }
          break;

        case 'organization':
          navigate(`/organization/${ref.name}`, { state: { organization: ref } });
          break;

        case 'contract':
          if (name === 'executive_rejected_contract' || name === 'executive_confirmed_contract') {
            navigate(`/organizacijos/perziuretos`);
          } else {
            navigate(`/peržiūrėti/${type}/${ref.id}`, { state: { contr: ref, notification: n } });
          }
          break;

        case 'request':
          if (name === 'new_service_order_request_waiting_for_review') {
            if (n.reference.data.tc_id === null) {
              navigate(`/užklausos/redaguoti/${ref.id}`);
            }
          } else if (name === 'new_service_order_request') {
            if (n.reference.data.orderId === null) {
              navigate(`/užklausos/visos`);
            }
          }
          break;

        default:
          break;
      }
    },
    [navigate]
  );

  const unread = notifications.unreadCount || 0;
  const filters = getFiltersForRole(userData?.role);
  const status = theme.palette.custom.status;
  const errorText = typeof error === 'string' ? error : error ? 'Nepavyko įkelti pranešimų.' : '';

  return (
    <>
      <Tooltip title="Pranešimai" placement={variant === 'header' ? 'bottom' : 'top'}>
        <IconButton
          ref={anchorRef}
          onClick={() => {
            const r = anchorRef.current?.getBoundingClientRect();
            if (r) setSide(r.left < window.innerWidth / 2 ? 'sidebar' : 'header');
            setOpen((p) => !p);
          }}
          aria-label={unread ? `Pranešimai, neperskaitytų: ${unread}` : 'Pranešimai'}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            flexShrink: 0,
            width: variant === 'sidebar-collapsed' ? 44 : 36,
            height: 36,
            borderRadius: '10px',
            color: open ? 'text.primary' : 'text.secondary',
            bgcolor: open ? 'action.hover' : 'transparent',
            ...(variant === 'header' && { ml: 1.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' })
          }}
        >
          <Badge
            badgeContent={unread}
            max={99}
            color="warning"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                fontWeight: 700,
                height: 16,
                minWidth: 16,
                px: '4px',
                boxShadow: `0 0 0 2px ${theme.palette.background.default}`
              }
            }}
          >
            <IconBell stroke={1.8} size={19} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={position.anchorOrigin}
        transformOrigin={position.transformOrigin}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Pranešimai',
          sx: {
            width: 'min(400px, calc(100vw - 24px))',
            maxHeight: 'min(640px, calc(100vh - 24px))',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...position.paperSx
          }
        }}
      >
        {/* header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.75, pb: 1.25 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
            Pranešimai
          </Typography>
          {unread > 0 && (
            <Chip
              size="small"
              label={unread}
              sx={{ height: 20, fontSize: 11.5, bgcolor: status.pending.bg, color: status.pending.fg, '& .MuiChip-label': { px: '7px' } }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Pažymėti viską kaip perskaitytą">
            <span>
              <IconButton aria-label="Pažymėti viską kaip perskaitytą" onClick={markAllAsRead} disabled={!unread} sx={{ width: 32, height: 32 }}>
                <IconChecks stroke={1.8} size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* filters */}
        <Box role="group" aria-label="Filtras" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 2, pb: 1.5 }}>
          {filters.map((f) => {
            const active = filter === f.value;
            return (
              <Chip
                key={f.value}
                label={f.label}
                size="small"
                clickable
                aria-pressed={active}
                onClick={() => setFilter(f.value)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  height: 26,
                  fontSize: 12,
                  ...(active
                    ? { bgcolor: 'text.primary', color: 'background.paper', '&:hover': { bgcolor: 'text.primary' } }
                    : { color: 'text.secondary' })
                }}
              />
            );
          })}
        </Box>
        <Divider />

        {errorText && (
          <Alert severity="error" sx={{ m: 1.5 }}>
            {errorText}
          </Alert>
        )}

        {/* list */}
        <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <NotificationList notifications={filteredNotifications} onItemClick={handleItemClick} user={userData} />
        </Box>

        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={() => {
              setOpen(false);
              navigate('/pranesimai');
            }}
          >
            Peržiūrėti viską
          </Button>
        </Box>
      </Popover>
    </>
  );
}

NotificationSection.propTypes = {
  variant: PropTypes.oneOf(['header', 'sidebar', 'sidebar-collapsed']),
  userData: PropTypes.object
};